import { FC, useState } from "react";

import { Search, Loader2, Filter } from "lucide-react";

import { Quote } from "@/_services/invoiceService";
import { StatusTag } from "@/modules/order/components/OrderInvoiceInfosPreview/components/StatusTag";

import { useQuoteAssociation } from "../hooks/useQuoteAssociation";

interface Props {
  quoteOption: string;
  searchQuery: string;
  setSearchQuery: (query: string) => void;
  loadingQuotes: boolean;
  filteredQuotes: Quote[];
  onQuoteSelection: (quoteId: string) => void;
  formatQuoteStatus: (status: string) => string;
  formatAmount: (amount: string, currency?: string) => string;
  filterStatus: string;
  setFilterStatus: (status: string) => void;
}

const ExistingQuoteSelection: FC<Props> = ({
  quoteOption,
  searchQuery,
  setSearchQuery,
  loadingQuotes,
  filteredQuotes,
  onQuoteSelection,
  formatQuoteStatus,
  formatAmount,
  filterStatus,
  setFilterStatus,
}) => {
  const { selectedQuote } = useQuoteAssociation();
  const [showSearchFilter, setShowSearchFilter] = useState<boolean>(false);

  if (quoteOption !== "existing") return null;

  return (
    <div className="space-y-4 mt-4">
      <div className="flex flex-col ">
        <div className="relative">
          <Search className="absolute left-3 top-3 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Rechercher un devis..."
            value={searchQuery}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
              setSearchQuery(e.target.value)
            }
            className="w-full pl-10 text-sm pr-4 py-2 border border-gray-300 rounded-lg focus:border-transparent"
          />
          <Filter
            className="absolute right-3 top-3 h-4 w-4 text-gray-400 cursor-pointer"
            onClick={() => {
              setShowSearchFilter(!showSearchFilter);
            }}
          />
        </div>
        {showSearchFilter && (
          <div className="space-y-2 flex align-self-end">
            <select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              className="mt-1 block border border-gray-300 rounded-lg shadow-sm focus:border-purple-500 p-1"
            >
              <option value="estimate_pending_status">En attente</option>
              <option value="estimate_accepted_status">Validé</option>
              <option value="estimate_invoiced_status">Facturé</option>
            </select>
          </div>
        )}
      </div>

      <div className="space-y-2 max-h-64 overflow-y-auto">
        {loadingQuotes ? (
          <div className="flex items-center justify-center p-4">
            <Loader2 className="w-4 h-4 animate-spin mr-2" />
            <span className="text-sm text-gray-600">
              Chargement des devis...
            </span>
          </div>
        ) : filteredQuotes?.length > 0 ? (
          filteredQuotes.map((quote) => (
            <div
              key={quote.id}
              className={`p-3 border rounded-lg cursor-pointer transition-all ${
                selectedQuote === quote.id
                  ? "border-purple-300 bg-purple-50"
                  : "border-gray-200 hover:border-gray-300"
              }`}
              onClick={() => onQuoteSelection(quote.id)}
            >
              <div className="flex items-center justify-between">
                <div>
                  <div className="font-medium text-sm">{quote.label}</div>
                  <div className="text-xs text-gray-500">
                    {quote.estimate_number}
                  </div>
                </div>
                <div className="text-right">
                  <div className="font-bold text-sm">
                    {formatAmount(quote.currency_amount, quote.currency)}
                  </div>
                  <StatusTag
                    className={`text-xs ${
                      quote.status === "estimate_accepted_status" ||
                      quote.status === "accepted"
                        ? "bg-green-100 text-green-700"
                        : quote.status === "estimate_pending_status" ||
                            quote.status === "pending"
                          ? "bg-orange-100 text-orange-700"
                          : quote.status === "estimate_invoiced_status"
                            ? "bg-gray-100 text-gray-700"
                            : "bg-blue-100 text-blue-700"
                    }`}
                    text={formatQuoteStatus(quote.status)}
                  />
                </div>
              </div>
            </div>
          ))
        ) : (
          <div className="text-center p-4 text-gray-500 text-sm">
            Aucun devis trouvé
          </div>
        )}
      </div>
    </div>
  );
};

export default ExistingQuoteSelection;
